import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Company } from "../lib/access";

interface Dept { id: string; name: string; slug: string }
interface Member { user_id: string; profiles: { full_name: string | null } | null }
interface Perm { key: string; module_key: string; description: string | null }
interface Person { user_id: string; profiles: { full_name: string | null } | null }

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

export function Departments({ company }: { company: Company }) {
  const [depts, setDepts] = useState<Dept[] | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [perms, setPerms] = useState<Perm[]>([]);
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const canManage = company.role === "super_admin" || company.role === "admin";
  const canGrant = company.role === "super_admin";

  const loadDepts = useCallback(async () => {
    const { data } = await supabase.from("departments")
      .select("id,name,slug").eq("company_id", company.id).order("name");
    const rows = (data as Dept[]) ?? [];
    setDepts(rows);
    setActive((a) => a ?? rows[0]?.id ?? null);
  }, [company.id]);

  useEffect(() => { void loadDepts(); }, [loadDepts]);

  useEffect(() => {
    supabase.from("permissions").select("key,module_key,description")
      .then(({ data }) => setPerms((data as Perm[]) ?? []));
    supabase.from("memberships").select("user_id,profiles(full_name)")
      .eq("company_id", company.id).eq("status", "active")
      .then(({ data }) => setPeople((data as unknown as Person[]) ?? []));
  }, [company.id]);

  const loadDetail = useCallback(async () => {
    if (!active) return;
    const [m, p] = await Promise.all([
      supabase.from("department_members").select("user_id,profiles(full_name)")
        .eq("department_id", active),
      supabase.from("department_permissions").select("permission_key")
        .eq("department_id", active),
    ]);
    setMembers((m.data as unknown as Member[]) ?? []);
    setGranted(new Set(((p.data as { permission_key: string }[]) ?? []).map((r) => r.permission_key)));
  }, [active]);

  useEffect(() => { void loadDetail(); }, [loadDetail]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    const { error } = await supabase.from("departments")
      .insert({ company_id: company.id, name, slug: slugify(name) });
    if (error) return setMsg(error.message);
    setName("");
    void loadDepts();
  }

  async function toggleMember(userId: string, on: boolean) {
    if (on) {
      await supabase.from("department_members")
        .insert({ company_id: company.id, department_id: active, user_id: userId });
    } else {
      await supabase.from("department_members").delete()
        .eq("department_id", active).eq("user_id", userId);
    }
    void loadDetail();
  }

  async function togglePerm(key: string, on: boolean) {
    setMsg(null);
    if (on) {
      const { error } = await supabase.from("department_permissions")
        .insert({ company_id: company.id, department_id: active, permission_key: key });
      if (error) setMsg("That permission belongs to a module your company does not have.");
    } else {
      await supabase.from("department_permissions").delete()
        .eq("department_id", active).eq("permission_key", key);
    }
    void loadDetail();
  }

  const owned = perms.filter((p) => company.modules.includes(p.module_key));
  const memberIds = new Set(members.map((m) => m.user_id));

  return (
    <>
      <header className="page-head">
        <h1>Departments</h1>
        <p className="muted">
          A department decides what its people can do, and whose calls they can see.
          Someone can belong to more than one.
        </p>
      </header>

      {canManage && (
        <form className="card row-form" onSubmit={create}>
          <div style={{ flex: 1 }}>
            <label htmlFor="dn2">New department</label>
            <input id="dn2" required value={name} placeholder="Sales"
              onChange={(e) => setName(e.target.value)} />
          </div>
          <button className="btn" type="submit" disabled={!name.trim()}>Create</button>
        </form>
      )}
      {msg && <p className="status error">{msg}</p>}

      {depts === null ? <p className="muted">Loading…</p>
        : depts.length === 0 ? <div className="card"><p className="muted">No departments yet.</p></div>
        : (
          <>
            <div className="tabs">
              {depts.map((d) => (
                <button key={d.id} className={d.id === active ? "on" : ""}
                  onClick={() => setActive(d.id)}>{d.name}</button>
              ))}
            </div>

            <section className="card">
              <h2>Who is in it</h2>
              {people.length === 0 ? <p className="muted">Invite people first.</p> : (
                <ul className="checks">
                  {people.map((p) => (
                    <li key={p.user_id}>
                      <label className="check">
                        <input type="checkbox" disabled={!canManage}
                          checked={memberIds.has(p.user_id)}
                          onChange={(e) => void toggleMember(p.user_id, e.target.checked)} />
                        {p.profiles?.full_name ?? p.user_id.slice(0, 8)}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="card">
              <h2>What it may do</h2>
              <p className="muted small">
                {canGrant ? "Only modules on your plan appear here."
                          : "Only a super admin can change these."}
              </p>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Can</th><th>Granted</th></tr></thead>
                  <tbody>
                    {owned.map((p) => (
                      <tr key={p.key}>
                        <td><b>{p.description ?? p.key}</b><span className="sub">{p.key}</span></td>
                        <td>
                          <input type="checkbox" disabled={!canGrant}
                            checked={granted.has(p.key)}
                            aria-label={p.description ?? p.key}
                            onChange={(e) => void togglePerm(p.key, e.target.checked)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
    </>
  );
}
